import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api, type Env, type Project } from "../../api";
import { city } from "../../components/ui";

// The project page's tabs other than Overview. Each answers one question about the project.

const usd = (n: number | null | undefined, d = 0) =>
  n === null || n === undefined ? "—" : `$${n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;

function Unavailable({ reason }: { reason: string }) {
  return <div className="empty">{reason}</div>;
}

function EnvPicker({ envs, value, onChange }: { envs: Env[]; value: string; onChange: (v: string) => void }) {
  if (envs.length < 2) return null;
  return (
    <select className="input" style={{ height: 32, width: 180 }} aria-label="Environment" value={value} onChange={(e) => onChange(e.target.value)}>
      {envs.map((e) => <option key={e.id} value={e.name}>{e.name} · {city(e.region)}</option>)}
    </select>
  );
}

/* ---------------- Infra: what it uses today, and production in Frankfurt ---------------- */

interface PlanItem { service: string; what: string; quantity: string; monthlyUsd: number; source: "price-list" | "estimate" }
interface Infra {
  today: {
    perMonthUsd: number | null;
    months: { month: string; usd: number }[];
    environments: { name: string; region: string; kind: string; services: { name: string; description?: string }[] }[];
    sharedServices: { slug: string; name: string; kind: string; region: string; monthlyUsd: number | null; usage: string }[];
  };
  defaults: Record<string, string | number>;
  prod: { region: string; city: string; options: Record<string, string | number>; items: PlanItem[]; monthlyUsd: number; basis: string };
}

export function InfraTab({ project }: { project: Project }) {
  const [opts, setOpts] = useState<Record<string, string>>({});
  const qs = new URLSearchParams(opts).toString();
  const q = useQuery({ queryKey: ["infra", project.slug, qs], queryFn: () => api<Infra>(`/projects/${project.slug}/infra${qs ? `?${qs}` : ""}`), placeholderData: (p) => p });
  if (q.isPending) return <div className="panel"><span className="skeleton" style={{ width: 300, height: 16 }} /></div>;
  if (q.error) return <div className="panel error">{q.error.message}</div>;
  const d = q.data;
  const o = d.prod.options;
  const set = (k: string) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => setOpts({ ...opts, [k]: e.target.value });
  const diff = d.today.perMonthUsd === null ? null : d.prod.monthlyUsd - d.today.perMonthUsd;

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <div className="kpis">
        <div className="kpi"><div className="kpi-label">Today</div><div className="kpi-value num">{usd(d.today.perMonthUsd)}<span className="unit">/mo</span></div><div className="kpi-note">average, {d.today.months.length} months measured</div></div>
        <div className="kpi"><div className="kpi-label">Production in {d.prod.city}</div><div className="kpi-value num">{usd(d.prod.monthlyUsd)}<span className="unit">/mo</span></div><div className="kpi-note">{o.instanceType} · database {String(o.db).replace("-", " ")}</div></div>
        <div className="kpi"><div className="kpi-label">A year in production</div><div className="kpi-value num">{usd(d.prod.monthlyUsd * 12)}</div><div className="kpi-note">{diff === null ? "no cost measured today" : `${diff >= 0 ? "+" : "−"}${usd(Math.abs(diff))} a month against today`}</div></div>
      </div>

      <div className="split">
        <section className="panel flush" aria-labelledby="prod-plan">
          <div className="panel-head"><h2 id="prod-plan">Production in {d.prod.city}</h2><span className="stamp mono">{d.prod.region}</span></div>
          <div className="env-body" style={{ paddingTop: 12 }}>
            <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
              <label className="field"><span>Box</span>
                <select className="input" value={String(o.instanceType)} onChange={set("instanceType")}>
                  {["t4g.small", "t4g.medium", "t4g.large", "t4g.xlarge"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="field"><span>Database</span>
                <select className="input" value={String(o.db)} onChange={set("db")}>
                  <option value="own-rds">Its own RDS</option><option value="shared-rds">Shared prod RDS</option>
                  <option value="in-box">Inside the box</option><option value="none">None</option>
                </select>
              </label>
              {(o.db === "own-rds" || o.db === "shared-rds") && (
                <label className="field"><span>Database size</span>
                  <select className="input" value={String(o.dbClass)} onChange={set("dbClass")}>
                    {["db.t4g.micro", "db.t4g.small", "db.t4g.medium"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </label>
              )}
              <label className="field"><span>Disk, GB</span><input className="input num" type="number" min={8} max={2000} value={String(o.rootGb)} onChange={set("rootGb")} /></label>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Service</th><th>What it is for</th><th className="num hide-phone">Quantity</th><th className="num">Per month</th></tr></thead>
              <tbody>
                {d.prod.items.map((i, n) => (
                  <tr key={n}>
                    <td className="cell-main">{i.service}</td>
                    <td>{i.what}{i.source === "estimate" && <span className="muted small"> · estimate</span>}</td>
                    <td className="num muted hide-phone">{i.quantity}</td>
                    <td className="num">{usd(i.monthlyUsd, 2)}</td>
                  </tr>
                ))}
                <tr><td colSpan={3}><strong>Total</strong></td><td className="num"><strong>{usd(d.prod.monthlyUsd, 2)}</strong></td></tr>
              </tbody>
            </table>
          </div>
          <div className="env-body" style={{ paddingTop: 12 }}><p className="stamp">{d.prod.basis}</p></div>
        </section>

        <aside className="panel" aria-labelledby="today">
          <h2 id="today" style={{ marginBottom: 12 }}>What it uses today</h2>
          {d.today.environments.map((e) => (
            <div key={e.name} style={{ marginBottom: 14 }}>
              <div><strong className="mono">{e.name}</strong> <span className="muted">· {city(e.region)} · {e.kind === "external" ? "outside the lab" : "on the lab host"}</span></div>
              <div className="cell-sub">{e.services.map((s) => s.name).join(" · ") || "no services recorded"}</div>
            </div>
          ))}
          <h3 style={{ margin: "16px 0 8px" }}>Shared services</h3>
          {d.today.sharedServices.length === 0 && <p className="muted">None recorded.</p>}
          <dl className="kv">
            {d.today.sharedServices.map((s) => (
              <><dt key={`t${s.slug}`}>{s.name}</dt><dd key={`d${s.slug}`} className="muted">{s.usage || s.kind}</dd></>
            ))}
          </dl>
          {d.today.months.length > 0 && (
            <>
              <h3 style={{ margin: "16px 0 8px" }}>Measured cost</h3>
              <dl className="kv">
                {d.today.months.map((m) => (
                  <><dt key={`m${m.month}`}>{new Date(m.month).toLocaleString("en-US", { month: "long", timeZone: "UTC" })}</dt><dd key={`v${m.month}`} className="num">{usd(m.usd, 2)}</dd></>
                ))}
              </dl>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ---------------- Credentials: the vault ---------------- */

interface Credential {
  id: string; name: string; kind: string; env: string | null; username: string; url: string; purpose: string;
  secretRef: string; location: string; inVault: boolean; owner: string | null; rotatedAt: string | null; updatedAt: string;
}

function Reveal({ id }: { id: string }) {
  const [value, setValue] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const reveal = useMutation({
    mutationFn: () => api<{ value: string }>(`/credentials/${id}/reveal`, { method: "POST" }),
    onSuccess: (r) => setValue(r.value),
  });
  useEffect(() => {
    if (value === null) return;
    const t = setTimeout(() => setValue(null), 30_000);
    return () => clearTimeout(t);
  }, [value]);
  if (value !== null) {
    return (
      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <code className="mono" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</code>
        <button className="btn ghost small" onClick={async () => { try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* selection fallback: the value is visible */ } }}>{copied ? "Copied" : "Copy"}</button>
        <button className="btn ghost small" onClick={() => setValue(null)}>Hide</button>
      </span>
    );
  }
  return (
    <span>
      <button className="btn small" onClick={() => reveal.mutate()} disabled={reveal.isPending}>{reveal.isPending ? "Revealing…" : "Reveal"}</button>
      {reveal.error && <span className="error small"> {reveal.error.message}</span>}
    </span>
  );
}

function SetValue({ cred, done }: { cred: Credential; done: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const save = useMutation({
    mutationFn: () => api(`/credentials/${cred.id}/value`, { method: "POST", body: JSON.stringify({ value }) }),
    onSuccess: () => { setValue(""); qc.invalidateQueries({ queryKey: ["credentials"] }); done(); },
  });
  return (
    <form style={{ display: "flex", gap: 8, flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <input className="input" type="password" autoComplete="off" aria-label={`Value for ${cred.name}`} placeholder="Paste the value" value={value} onChange={(e) => setValue(e.target.value)} style={{ height: 32, width: 240 }} required />
      <button className="btn primary small" disabled={save.isPending}>Store in the vault</button>
      <button type="button" className="btn ghost small" onClick={done}>Cancel</button>
      {save.error && <span className="error small">{save.error.message}</span>}
    </form>
  );
}

function AddCredential({ project, done }: { project: Project; done: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", kind: "login", env: project.environments[0]?.name ?? "", username: "", url: "", purpose: "", location: "" });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const add = useMutation({
    mutationFn: () => api(`/projects/${project.slug}/credentials`, { method: "POST", body: JSON.stringify({ ...f, location: f.location || "to be stored in the vault" }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["credentials"] }); done(); },
  });
  return (
    <form className="panel" style={{ display: "grid", gap: 12 }} onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
      <h3>New credential</h3>
      <div className="form-grid">
        <label className="field"><span>Name</span><input className="input" value={f.name} onChange={set("name")} placeholder="Strapi admin login" required /></label>
        <label className="field"><span>Kind</span>
          <select className="input" value={f.kind} onChange={set("kind")}>{["login", "database", "api-key", "service", "certificate", "other"].map((k) => <option key={k}>{k}</option>)}</select>
        </label>
        <label className="field"><span>Environment</span>
          <select className="input" value={f.env} onChange={set("env")}><option value="">All</option>{project.environments.map((e) => <option key={e.id}>{e.name}</option>)}</select>
        </label>
        <label className="field"><span>Username</span><input className="input" value={f.username} onChange={set("username")} /></label>
        <label className="field"><span>Where it is used (URL)</span><input className="input" value={f.url} onChange={set("url")} placeholder="https://admin-acme.scdev.io" /></label>
        <label className="field"><span>What it is for</span><input className="input" value={f.purpose} onChange={set("purpose")} /></label>
      </div>
      {add.error && <p className="error">{add.error.message}</p>}
      <div className="actions"><button className="btn primary" disabled={add.isPending}>Add</button><button type="button" className="btn" onClick={done}>Cancel</button></div>
      <p className="stamp">The value is never typed here: add the credential, then use “Store in the vault” on its row.</p>
    </form>
  );
}

export function CredentialsTab({ project }: { project: Project }) {
  const q = useQuery({ queryKey: ["credentials", project.slug], queryFn: () => api<Credential[]>(`/projects/${project.slug}/credentials`) });
  const [setting, setSetting] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const outside = q.data?.filter((c) => !c.inVault).length ?? 0;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="spread">
        <p className="muted">{q.data ? `${q.data.length} credentials · ${q.data.length - outside} in the vault${outside ? ` · ${outside} still kept elsewhere` : ""}` : ""}</p>
        {!adding && <button className="btn" onClick={() => setAdding(true)}>Add credential</button>}
      </div>
      {adding && <AddCredential project={project} done={() => setAdding(false)} />}
      <section className="panel flush" aria-label="Credentials">
        {q.error && <div className="item"><span className="error">{q.error.message}</span></div>}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Credential</th><th>Environment</th><th>Username · URL</th><th>Where the value is</th><th className="num">Value</th></tr></thead>
            <tbody>
              {q.data?.map((c) => (
                <tr key={c.id}>
                  <td><div className="cell-main">{c.name}</div><div className="cell-sub">{c.kind}{c.purpose ? ` · ${c.purpose}` : ""}</div></td>
                  <td className="mono">{c.env ?? "all"}</td>
                  <td>
                    <div>{c.username || <span className="muted">—</span>}</div>
                    {c.url && <a className="cell-sub" href={c.url} target="_blank" rel="noreferrer" style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>{c.url.replace(/^https?:\/\//, "")}</a>}
                  </td>
                  <td>
                    {c.inVault ? <span className="health up"><span className="dot" />Vault <span className="mono small muted">{c.secretRef.replace(/^ssm:/, "")}</span></span>
                      : <span className="pill warning" title={c.location}>Outside the vault</span>}
                    {!c.inVault && <div className="cell-sub">{c.location}</div>}
                  </td>
                  <td className="num">
                    {setting === c.id ? <SetValue cred={c} done={() => setSetting(null)} />
                      : c.inVault ? <span style={{ display: "inline-flex", gap: 6 }}><Reveal id={c.id} /><button className="btn ghost small" onClick={() => setSetting(c.id)}>Replace</button></span>
                      : <button className="btn small" onClick={() => setSetting(c.id)}>Store in the vault</button>}
                  </td>
                </tr>
              ))}
              {q.data?.length === 0 && <tr><td colSpan={5}><div className="empty">No credentials recorded for this project.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <p className="stamp">Values live in AWS (SSM Parameter Store). Revealing one is recorded in the audit log, with who and when; it hides again after 30 seconds.</p>
    </div>
  );
}

/* ---------------- Uses: AWS resources tagged with the project ---------------- */

interface Resources {
  available: boolean; reason?: string; checkedAt?: string; tagValues?: string[]; regions?: string[];
  byService?: Record<string, number>;
  resources?: { arn: string; service: string; type: string; region: string; name: string; tags: Record<string, string> }[];
}

export function UsesTab({ project }: { project: Project }) {
  const q = useQuery({ queryKey: ["resources", project.slug], queryFn: () => api<Resources>(`/projects/${project.slug}/resources`) });
  const [svc, setSvc] = useState("");
  const rows = useMemo(() => (q.data?.resources ?? []).filter((r) => !svc || r.service === svc), [q.data, svc]);
  if (q.isPending) return <div className="panel"><span className="skeleton" style={{ width: 280, height: 16 }} /></div>;
  if (q.error) return <div className="panel error">{q.error.message}</div>;
  if (!q.data.available) return <Unavailable reason={q.data.reason ?? "Couldn't read AWS."} />;
  const by = Object.entries(q.data.byService ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <p className="muted">
        Every AWS resource tagged <span className="mono">Project={q.data.tagValues?.join(" or ")}</span> in {q.data.regions?.map(city).join(", ")}.
        Untagged resources don't show here; the Alerts page lists projects whose boxes carry no tags.
      </p>
      <div className="tabs" role="tablist" aria-label="Service">
        <button role="tab" className="tab" aria-selected={!svc} onClick={() => setSvc("")}>All<span className="n">{q.data.resources?.length ?? 0}</span></button>
        {by.map(([s, n]) => <button key={s} role="tab" className="tab" aria-selected={svc === s} onClick={() => setSvc(s)}>{s}<span className="n">{n}</span></button>)}
      </div>
      <section className="panel flush" aria-label="Resources">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Service · type</th><th>Where</th><th className="hide-phone">Environment</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.arn}>
                  <td className="mono truncate" style={{ maxWidth: 340 }} title={r.arn}>{r.name}</td>
                  <td>{r.service} <span className="muted">· {r.type}</span></td>
                  <td>{r.region === "global" ? "Global" : city(r.region)}</td>
                  <td className="hide-phone">{r.tags.Environment ?? <span className="muted">—</span>}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4}><div className="empty">Nothing tagged with this project{svc ? ` in ${svc}` : ""}.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Logs ---------------- */

interface Logs { available: boolean; reason?: string; services?: { service: string; state?: string }[]; log?: string }

export function LogsTab({ project }: { project: Project }) {
  const [env, setEnv] = useState(project.environments[0]?.name ?? "");
  const [service, setService] = useState("");
  const [tail, setTail] = useState("200");
  const q = useQuery({
    queryKey: ["logs", project.slug, env, service, tail],
    queryFn: () => api<Logs>(`/projects/${project.slug}/envs/${env}/logs?tail=${tail}${service ? `&service=${service}` : ""}`),
    enabled: Boolean(env),
  });
  const text = q.data?.log ?? "";
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="toolbar">
        <div className="actions">
          <EnvPicker envs={project.environments} value={env} onChange={setEnv} />
          {q.data?.services && q.data.services.length > 0 && (
            <select className="input" style={{ height: 32, width: 180 }} aria-label="Service" value={service} onChange={(e) => setService(e.target.value)}>
              <option value="">All services</option>
              {q.data.services.map((s) => <option key={s.service}>{s.service}</option>)}
            </select>
          )}
          <select className="input" style={{ height: 32, width: 130 }} aria-label="Lines" value={tail} onChange={(e) => setTail(e.target.value)}>
            {["100", "200", "500", "2000"].map((n) => <option key={n} value={n}>Last {n}</option>)}
          </select>
        </div>
        <button className="btn" onClick={() => q.refetch()} disabled={q.isFetching}>{q.isFetching ? "Reading…" : "Refresh"}</button>
      </div>
      {project.environments.length === 0 && <Unavailable reason="No environment yet." />}
      {q.error && <div className="panel error">{q.error.message}</div>}
      {q.data && !q.data.available && <Unavailable reason={q.data.reason ?? "Logs aren't available."} />}
      {q.data?.available && (
        <pre className="mono" style={{ margin: 0, background: "#0f1419", color: "#d7dde5", borderRadius: 8, padding: 16, maxHeight: 560, overflow: "auto", whiteSpace: "pre-wrap", fontSize: 12, lineHeight: "18px" }}>
          {text || "No log lines."}
        </pre>
      )}
    </div>
  );
}

/* ---------------- Access: who uses it, and when it was last used ---------------- */

interface HostAccess {
  host: string; lastRequestAt: string | null; requests24h: number; requests7d: number; requests30d: number;
  clients7d: number; errorRate7d: number; topPaths7d: { path: string; requests: number }[]; daily: { date: string; requests: number }[];
}
interface Access { available: boolean; reason?: string; hosts?: HostAccess[] }

function Daily({ days }: { days: { date: string; requests: number }[] }) {
  const max = Math.max(1, ...days.map((d) => d.requests));
  const W = 300, H = 48, bw = W / Math.max(days.length, 1);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Requests per day, last 30 days">
      {days.map((d, i) => <rect key={d.date} x={i * bw + 1} width={Math.max(1, bw - 2)} y={H - (d.requests / max) * H} height={(d.requests / max) * H} fill="var(--accent)" rx="1"><title>{`${d.date}: ${d.requests}`}</title></rect>)}
    </svg>
  );
}

export function AccessTab({ project }: { project: Project }) {
  const [env, setEnv] = useState(project.environments[0]?.name ?? "");
  const q = useQuery({ queryKey: ["access", project.slug, env], queryFn: () => api<Access>(`/projects/${project.slug}/envs/${env}/access`), enabled: Boolean(env) });
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="toolbar"><EnvPicker envs={project.environments} value={env} onChange={setEnv} /></div>
      {q.data && !q.data.available && <Unavailable reason={q.data.reason ?? "No access data."} />}
      {q.error && <div className="panel error">{q.error.message}</div>}
      {q.data?.hosts?.map((h) => (
        <section className="panel" key={h.host} aria-label={h.host}>
          <div className="spread" style={{ marginBottom: 12 }}><h3 className="mono">{h.host}</h3><span className="stamp">last request {h.lastRequestAt ? new Date(h.lastRequestAt).toLocaleString() : "never"}</span></div>
          <div className="kpis" style={{ marginBottom: 16 }}>
            <div className="kpi"><div className="kpi-label">Last 24 h</div><div className="kpi-value num">{h.requests24h.toLocaleString()}</div></div>
            <div className="kpi"><div className="kpi-label">Last 7 days</div><div className="kpi-value num">{h.requests7d.toLocaleString()}</div><div className="kpi-note">{h.clients7d} distinct visitors</div></div>
            <div className="kpi"><div className="kpi-label">Last 30 days</div><div className="kpi-value num">{h.requests30d.toLocaleString()}</div></div>
            <div className="kpi"><div className="kpi-label">Server errors, 7 d</div><div className="kpi-value num">{(h.errorRate7d * 100).toFixed(1)}<span className="unit">%</span></div></div>
          </div>
          <Daily days={h.daily} />
          {h.topPaths7d.length > 0 && (
            <dl className="kv" style={{ marginTop: 12, gridTemplateColumns: "minmax(0,1fr) auto" }}>
              {h.topPaths7d.map((p) => <><dt key={`p${p.path}`} className="mono">{p.path}</dt><dd key={`n${p.path}`} className="num">{p.requests.toLocaleString()}</dd></>)}
            </dl>
          )}
        </section>
      ))}
    </div>
  );
}

/* ---------------- Deploy: CI tokens, history, deploy now ---------------- */

interface DeployRow { id: string; ref: string; sha: string | null; source: string; actor: string | null; status: string; startedAt: string; finishedAt: string | null; logTail?: string }
interface TokenRow { id: string; label: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }

export function DeployTab({ project }: { project: Project }) {
  const qc = useQueryClient();
  const [env, setEnv] = useState(project.environments[0]?.name ?? "");
  const current = project.environments.find((e) => e.name === env);
  const runsHere = current?.kind === "app";
  const deploys = useQuery({ queryKey: ["deploys", project.slug, env], queryFn: () => api<DeployRow[]>(`/projects/${project.slug}/envs/${env}/deploys`), enabled: Boolean(env), refetchInterval: 10_000 });
  const tokens = useQuery({ queryKey: ["tokens", project.slug, env], queryFn: () => api<TokenRow[]>(`/projects/${project.slug}/envs/${env}/deploy-tokens`), enabled: Boolean(env) });
  const [newToken, setNewToken] = useState<string | null>(null);
  const [ref, setRef] = useState("main");
  const mint = useMutation({
    mutationFn: () => api<{ token: string }>(`/projects/${project.slug}/envs/${env}/deploy-tokens`, { method: "POST" }),
    onSuccess: (r) => { setNewToken(r.token); qc.invalidateQueries({ queryKey: ["tokens"] }); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/projects/${project.slug}/envs/${env}/deploy-tokens/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tokens"] }),
  });
  const deploy = useMutation({
    mutationFn: () => api(`/projects/${project.slug}/envs/${env}/deploy`, { method: "POST", body: JSON.stringify({ ref }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deploys"] }),
  });
  const snippet = `pipelines:
  branches:
    main:
      - step:
          name: Deploy to the lab (${env})
          deployment: test
          script:
            - curl -fsS -X POST https://lab.scdev.io/api/deploy -H "Authorization: Bearer $LAB_DEPLOY_TOKEN" -H "content-type: application/json" -d "{\\"ref\\": \\"$BITBUCKET_COMMIT\\"}"`;

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <div className="toolbar"><EnvPicker envs={project.environments} value={env} onChange={setEnv} /></div>
      {!runsHere && <Unavailable reason={`${env || "This environment"} runs outside the lab, so the lab doesn't deploy it yet. Moving it into the lab makes deploys, logs and access available here.`} />}

      {runsHere && (
        <section className="panel" aria-labelledby="deploy-now">
          <h2 id="deploy-now" style={{ marginBottom: 12 }}>Deploy now</h2>
          <form className="actions" onSubmit={(e) => { e.preventDefault(); deploy.mutate(); }}>
            <input className="input mono" style={{ height: 32, width: 240 }} aria-label="Branch, tag or commit" value={ref} onChange={(e) => setRef(e.target.value)} />
            <button className="btn primary" disabled={deploy.isPending}>{deploy.isPending ? "Starting…" : `Deploy ${ref} to ${env}`}</button>
          </form>
          {deploy.error && <p className="error" style={{ marginTop: 8 }}>{deploy.error.message}</p>}
        </section>
      )}

      <div className="split">
        <section className="panel flush" aria-labelledby="history">
          <div className="panel-head"><h2 id="history">Deploys</h2><span className="stamp num">{deploys.data?.length ?? 0}</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>Commit</th><th>From</th><th>Result</th></tr></thead>
              <tbody>
                {deploys.data?.map((d) => (
                  <tr key={d.id}>
                    <td className="num muted">{new Date(d.startedAt).toLocaleString()}</td>
                    <td className="mono">{d.ref}{d.sha ? ` · ${d.sha.slice(0, 8)}` : ""}</td>
                    <td>{d.source}{d.actor ? ` · ${d.actor}` : ""}</td>
                    <td><span className={`health ${d.status === "succeeded" ? "up" : d.status === "failed" ? "down" : ""}`}><span className="dot" />{d.status}</span></td>
                  </tr>
                ))}
                {deploys.data?.length === 0 && <tr><td colSpan={4}><div className="empty">No deploys yet.</div></td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="panel" aria-labelledby="ci">
          <h2 id="ci" style={{ marginBottom: 8 }}>Bitbucket CI</h2>
          <p className="muted" style={{ marginBottom: 12 }}>A deploy token lets this repository's pipeline deploy {env} to the lab. Store it as the secured variable <span className="mono">LAB_DEPLOY_TOKEN</span>.</p>
          {newToken && (
            <div className="panel" style={{ background: "var(--raised)", marginBottom: 12 }}>
              <p className="small" style={{ marginBottom: 6 }}>Copy it now; it won't be shown again.</p>
              <code className="mono" style={{ wordBreak: "break-all" }}>{newToken}</code>
            </div>
          )}
          <button className="btn" onClick={() => mint.mutate()} disabled={mint.isPending || !env}>New deploy token</button>
          {mint.error && <p className="error small">{mint.error.message}</p>}
          <dl className="kv" style={{ marginTop: 12 }}>
            {tokens.data?.filter((t) => !t.revokedAt).map((t) => (
              <><dt key={`t${t.id}`}>{t.label}</dt><dd key={`d${t.id}`}>{t.lastUsedAt ? `used ${new Date(t.lastUsedAt).toLocaleDateString()}` : "never used"} · <button className="btn ghost small" onClick={() => revoke.mutate(t.id)}>Revoke</button></dd></>
            ))}
          </dl>
          <h3 style={{ margin: "16px 0 6px" }}>Pipeline step</h3>
          <pre className="mono" style={{ margin: 0, fontSize: 12, background: "var(--raised)", padding: 12, borderRadius: 8, overflowX: "auto" }}>{snippet}</pre>
        </aside>
      </div>
    </div>
  );
}
